import {
  ConfigItem,
  ConfigItemType,
  ConfigItemValue,
  MultiSelectConfigItem,
  PageView,
  SelectConfigItem,
  isConfigGroup,
  isDataTypeControl,
} from '@ecosystem/newron-client-lib';

import { KnownConfigItemType, MultiEditCallbacks, MultiEditInsight } from '../../../public/MultiEditRegister';
import { AllowedTypes, MultiEditConfigItem } from '../../../public/MultiEditRegister/types';
import {
  CollectionDataType,
  ConfigItemCallbacks,
  ConfigItemsInfo,
  GetInsightInfoFn,
  InsightDataInfo,
  InsightTag,
  ViewStateInfo,
} from '../../../shared/types';

import { PngRendererData } from './PngRenderer';
import {
  ConfigKeys,
  PngRendererConfigItems,
  applyRuleBook,
  fillMissingOptions,
  findNestedConfigItem,
  getAppliedValues,
  getConfigValueFromConfigItem,
  getDataTypeFromConfigValues,
  getValueFromConfigValue,
  patchConfigItem,
  patchConfigItems,
  updateConfigItemsValues,
} from './PngRendererUtils';

const getConfigItems = (
  {
    data,
    dataType,
  }: {
    data: PngRendererData;
    dataType?: CollectionDataType;
  },
  configItems?: ConfigItem[]
): ConfigItemsInfo => {
  const { configValues = [], allowDataTypeSwitch } = data ?? {};
  const configValuesFromInsight = allowDataTypeSwitch
    ? [...configValues, { key: ConfigKeys.DataTypeControl, value: dataType }]
    : configValues;

  const allowedConfigItems = applyRuleBook(
    configItems ? patchConfigItems(configItems, PngRendererConfigItems) : PngRendererConfigItems,
    configValuesFromInsight
  );
  const updatedItems = updateConfigItemsValues(allowedConfigItems, configValuesFromInsight);
  return { configItems: fillMissingOptions(updatedItems) };
};

const getInsightData = ({
  data,
  configItems,
}: {
  data: PngRendererData;
  configItems: ConfigItem[];
}): InsightDataInfo<PngRendererData> => {
  const configValues = configItems.flatMap(getConfigValueFromConfigItem).filter(configValue => !!configValue.value);
  return {
    data: {
      ...data,
      configValues: configValues.filter(configValue => configValue.key !== ConfigKeys.DataTypeControl),
    },
    dataType: getDataTypeFromConfigValues(configValues),
  };
};

const innerApplyConfigValue = ({
  data,
  configItems,
  newValue,
}: {
  data: PngRendererData;
  configItems: ConfigItem[];
  newValue: ConfigItemValue;
}): ConfigItemsInfo => {
  const configValues = configItems
    .flatMap(getConfigValueFromConfigItem)
    .map(val => (val.key === newValue.key ? newValue : val))
    .filter(val => !!val.value);

  return getConfigItems(
    getInsightData({ data, configItems: updateConfigItemsValues(configItems, configValues) }),
    configItems
  );
};

const applyConfigValue = async ({
  data,
  configItems,
  newValue,
}: {
  data: PngRendererData;
  configItems: ConfigItem[];
  newValue: ConfigItemValue;
}): Promise<ConfigItemsInfo> => {
  if (data.hasTimeout) {
    await new Promise(resolve => setTimeout(resolve, 3000));
  }

  return innerApplyConfigValue({ data, configItems, newValue });
};

const clearConfigValues = ({
  configItems,
  data,
}: {
  data: PngRendererData;
  configItems: ConfigItem[];
}): ConfigItemsInfo => {
  return {
    configItems: fillMissingOptions(
      applyRuleBook(
        configItems.map(configItem => {
          if (isConfigGroup(configItem)) {
            return {
              ...configItem,
              value: clearConfigValues({ data, configItems: configItem.value }).configItems,
            };
          }

          if (configItem.key !== ConfigKeys.Country) {
            delete configItem.value;
          }

          return configItem;
        }),
        [
          {
            key: ConfigKeys.Country,
            value: (
              configItems.find(
                item => item.key === ConfigKeys.Country && item.type === ConfigItemType.Select
              ) as SelectConfigItem
            )?.value,
          },
        ]
      )
    ),
  };
};

const onRequestOptions = async ({
  configItems,
  key,
}: {
  configItems: ConfigItem[];
  key: string;
}): Promise<ConfigItemsInfo> => {
  if ([ConfigKeys.LazyLoadSelect, ConfigKeys.LazyLoadMultiSelect].includes(key as ConfigKeys)) {
    const configItem = findNestedConfigItem(key as ConfigKeys, configItems) as MultiSelectConfigItem;
    if (configItem?.isLoading) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const updatedConfigItem = {
        ...configItem,
        options: [
          { value: 'option1', label: 'Option 1' },
          { value: 'option2', label: 'Option 2' },
          { value: 'option3', label: 'Option 3' },
        ],
        isLoading: false,
      };
      return {
        configItems: patchConfigItem(updatedConfigItem, configItems),
      };
    }
  }
  return { configItems };
};

const applyMultiEditConfigValue = ({
  data,
  newValue,
}: {
  data: PngRendererData;
  newValue: ConfigItemValue;
}): InsightDataInfo<PngRendererData> => {
  if (data.hasPropositionError) {
    throw new Error('Error applying multiedit config value (enforced)');
  }
  const configItemApplied = innerApplyConfigValue({
    data,
    configItems: getConfigItems({ data }).configItems,
    newValue,
  }).configItems;
  delete findNestedConfigItem(ConfigKeys.Age, configItemApplied)?.value;

  const insightData = getInsightData({
    data,
    configItems: configItemApplied,
  });

  return {
    ...insightData,
    data: {
      ...insightData.data,
      ...data.overrides,
      overrides: undefined,
    },
  };
};

const getViewStateInfo = ({
  data,
}: {
  data: PngRendererData;
  pageView: Pick<PageView<PngRendererData>, 'data'>;
}): ViewStateInfo<object> => {
  return {
    viewState: {
      configValues: data.configValues,
    },
    meta: {
      pathname: '/platform-playground/market',
      search: '',
    },
  };
};

export const configItemCallbacks: ConfigItemCallbacks<PngRendererData> = {
  getConfigItems,
  getInsightData,
  applyConfigValue,
  clearConfigValues,
  onRequestOptions,
  getViewStateInfo,
  applyMultiEditConfigValue,
};

export const getInsightInfo: GetInsightInfoFn<PngRendererData> = data => {
  if (data.shouldFailInsightInfo) {
    throw new Error('Error loading insight info (enforced)');
  }
  return {
    tags: (data.configValues ?? [])
      .map<InsightTag>(tag => {
        const configItem = findNestedConfigItem(tag.key);
        return {
          key: configItem && 'label' in configItem ? configItem.label : String(tag.key),
          value: getValueFromConfigValue(tag),
        };
      })
      .filter(tag => tag.value),
    trackingTags: {},
    chartName: data.chartName || '',
    helperText: data.helperText || '',
  };
};

export const multiEditCallbacks: MultiEditCallbacks = {
  getAllowedTypes: (): AllowedTypes => {
    const allowedTypes: AllowedTypes = [KnownConfigItemType.ProductGroup];
    Object.values(ConfigKeys).forEach(key => {
      const configItem = findNestedConfigItem(key);
      if (!isConfigGroup(configItem) && !isDataTypeControl(configItem) && key !== ConfigKeys.Context) {
        allowedTypes.push({
          type: key,
          title: configItem.label,
          label: configItem.label,
        });
      }
    });
    return allowedTypes;
  },
  getConfigItemInfo: async (configItemType, insights: MultiEditInsight<PngRendererData>[]) => {
    if (insights.some(insight => insight.data.hasConfigItemsError)) {
      throw new Error('Error getting config items (enforced)');
    }
    const ConfigItemInfoProvider = (key: ConfigKeys) => ({
      configItem: findNestedConfigItem(key) as MultiEditConfigItem,
      configValues: insights.some(insight => insight.data.hasConfigValueError)
        ? undefined
        : getAppliedValues(insights, key),
    });

    switch (configItemType) {
      case KnownConfigItemType.ProductGroup:
        return ConfigItemInfoProvider(ConfigKeys.Context);
      default:
        return ConfigItemInfoProvider(configItemType as ConfigKeys);
    }
  },
};
